import { createTheme } from '@mui/material/styles';
import { FONT_FAMILY } from 'common/sharedFunctions';

// Modern color palette
const modernColors = {
  primary: '#1976d2',
  secondary: '#F9FAFB',
  success: '#2e7d32',
  warning: '#ff9800',
  error: '#d32f2f',
  info: '#0288d1',
  textPrimary: '#37474f',
  textSecondary: '#546e7a',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  border: '#e0e0e0',
  hover: '#f5f5f5',
  headerBg: '#f5f7fa'
};

const theme = createTheme({
  palette: {
    primary: {
      main: modernColors.primary
    },
    secondary: {
      main: modernColors.secondary
    },
    text: {
      primary: modernColors.textPrimary,
      secondary: modernColors.textSecondary
    }
  },
  components: {
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontFamily: FONT_FAMILY,
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          padding: '16px',
          fontSize: '0.875rem',
          borderBottom: `1px solid ${modernColors.border}`,
          color: modernColors.textPrimary,
          '&:first-of-type': {
            paddingLeft: '24px'
          },
          '&:last-of-type': {
            paddingRight: '24px'
          }
        },
        head: {
          fontWeight: 600,
          backgroundColor: modernColors.headerBg,
          color: modernColors.textPrimary,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          '&:first-of-type': {
            borderTopLeftRadius: '8px'
          },
          '&:last-of-type': {
            borderTopRightRadius: '8px'
          }
        },
        body: {
          fontSize: '0.875rem',
          transition: 'background-color 0.2s ease'
        }
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${modernColors.border}`,
          overflow: 'hidden'
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          backgroundColor: modernColors.headerBg,
          '& .MuiTableRow-root': {
            height: '56px'
          }
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: modernColors.hover
            },
            '&:nth-of-type(even)': {
              backgroundColor: modernColors.surface
            }
          }
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          transition: 'all 0.2s ease',
          height: '52px',
          '&.Mui-selected': {
            backgroundColor: `${modernColors.primary}10 !important`,
            '&:hover': {
              backgroundColor: `${modernColors.primary}20 !important`
            }
          }
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          color: modernColors.textPrimary,
          borderTop: `1px solid ${modernColors.border}`
        },
        selectLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.875rem'
        },
        displayedRows: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.875rem'
        },
        select: {
          paddingTop: '8px',
          paddingBottom: '8px'
        },
        actions: {
          '& .MuiIconButton-root': {
            padding: '8px',
            color: modernColors.textPrimary
          }
        }
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          backgroundColor: modernColors.background
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          textTransform: 'none',
          borderRadius: '6px',
          fontWeight: 500,
          padding: '6px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.875rem'
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          padding: '8px',
          borderRadius: '6px'
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.875rem',
          transform: 'translate(14px, 12px) scale(1)',
          '&.Mui-focused, &.MuiFormLabel-filled': {
            transform: 'translate(14px, -9px) scale(0.75)'
          }
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.875rem'
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.875rem',
          minHeight: '42px',
          padding: '8px 16px'
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.875rem'
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.875rem',
          borderRadius: '6px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: modernColors.border
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: modernColors.primary
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px'
          }
        },
        input: {
          padding: '12px 14px'
        }
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: FONT_FAMILY,
          fontSize: '0.75rem',
          backgroundColor: modernColors.textPrimary,
          borderRadius: '4px',
          padding: '8px 12px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)'
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }
      }
    }
  },
});

export default theme;